import { useEffect, useState, useSyncExternalStore } from 'react';
import { useAuthStore, type AuthUser } from '../../store/authStore';
import { getMyProfile, patchMyProfile, type PatchProfileInput } from '../../api/users';
import { AvatarUploader } from '../../components/AvatarUploader';
import { Mail, User, Briefcase, Phone, Check } from '../../icons';
import { tSettings, getSettingsLang, subscribeSettingsLang, type SettingsI18nKey } from '../../lib/settings-i18n';

/**
 * settings-module-v1 SET-005 + design-20260606 §17.1/§17.4 — 프로필 설정.
 *   - 진입 시 GET /users/me 로 일괄 로드(조회 R).
 *   - [히어로] 아바타 + 이름·권한·이메일  /  [기본 정보] 이름·직책·전화(편집).
 *   - 사진은 AvatarUploader(업로드→wcfile:{fileId}).
 *   - 저장 시 PATCH /users/:id → authStore(name·avatarUrl) 갱신 → 전화면 반영.
 */

const ROLE_LABEL_KEY: Record<AuthUser['role'], SettingsI18nKey> = {
  STAFF: 'settings.profile.role_staff',
  MANAGER: 'settings.profile.role_manager',
  ADMIN: 'settings.profile.role_admin',
  SYSTEM_ADMIN: 'settings.profile.role_system_admin',
};

/**
 * `variant='fields-only'` — 히어로(아바타 블록)를 빼고 편집 폼만 그린다(2026-08-20).
 * 새 프로필 화면이 자기 히어로를 따로 그리기 때문이며, 기본값은 종전 그대로라 회귀가 없다.
 */
export function ProfileSection({ variant = 'full' }: { variant?: 'full' | 'fields-only' } = {}) {
  useSyncExternalStore(subscribeSettingsLang, getSettingsLang, getSettingsLang);
  const user = useAuthStore((s) => s.user);
  const [name, setName] = useState(user?.name ?? '');
  const [position, setPosition] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState(user?.email ?? '');
  // 프로필 새 항목 6종 (migration 106, 2026-08-20) — 전부 선택 입력.
  const [nameEn, setNameEn] = useState('');
  const [extension, setExtension] = useState('');
  const [deskLocation, setDeskLocation] = useState('');
  const [bio, setBio] = useState('');
  const [birthday, setBirthday] = useState('');
  const [timezone, setTimezone] = useState('');
  const [avatarUrl, setAvatarUrl] = useState<string | null>(user?.avatarUrl ?? null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    getMyProfile()
      .then((p) => {
        if (cancelled) return;
        setName(p.name);
        setPosition(p.position ?? '');
        setPhone(p.phone ?? '');
        setEmail(p.email ?? '');
        setAvatarUrl(p.avatarUrl ?? null);
        setNameEn(p.nameEn ?? '');
        setExtension(p.extension ?? '');
        setDeskLocation(p.deskLocation ?? '');
        setBio(p.bio ?? '');
        setBirthday(p.birthday ?? '');
        setTimezone(p.timezone ?? '');
      })
      .catch(() => { /* best-effort */ });
    return () => { cancelled = true; };
  }, []);

  if (!user) {
    return (
      <section className="settings-section" aria-labelledby="prof-h2">
        <h2 id="prof-h2">{tSettings('settings.profile.heading')}</h2>
        <p>{tSettings('settings.profile.login_required')}</p>
      </section>
    );
  }

  // 아바타는 AvatarUploader 가 서버에 즉시 저장(PUT/DELETE) → 여기선 display state + authStore 동기화만.
  function onAvatarChange(next: string | null) {
    setAvatarUrl(next);
    useAuthStore.setState((prev) => ({
      ...prev,
      user: prev.user ? { ...prev.user, avatarUrl: next } : null,
    }));
  }

  async function onSave() {
    setError(null);
    // ★ 생일 형식 검사는 뺐다(2026-09-13) — 생일은 이제 본인이 못 고치는 칸이라 싣지 않는다.
    //   남겨 두면 인사가 넣은 값이 형식에 어긋날 때 **다른 칸 저장까지** 막힌다.
    setSaving(true);
    try {
      // 신상 필드만 — 아바타는 별도 엔드포인트에서 이미 영속화됨.
      const input: PatchProfileInput = {
        // ★ 직위·이름·영문 이름·생일은 보내지 않는다 — 본인이 못 바꾸는 칸이다(서버 403
        //   `PROFILE_FIELD_READONLY`, 2026-09-13 사용자 결정). 타입에서도 빠져 있다.
        phone: phone.trim() || null,
        // 빈 칸은 null 로 보낸다 — "지웠다" 와 "안 건드렸다" 를 서버가 구분할 수 있어야 한다.
        extension: extension.trim() || null,
        deskLocation: deskLocation.trim() || null,
        bio: bio.trim() || null,
        timezone: timezone.trim() || null,
      };
      const updated = await patchMyProfile(String(user!.id), input);
      useAuthStore.setState((prev) => ({
        ...prev,
        user: prev.user ? { ...prev.user, name: updated.name } : null,
      }));
      setSavedAt(Date.now());
    } catch (err: any) {
      setError(tSettings('settings.profile.err_save', {
        msg: err.message ?? tSettings('settings.profile.err_unknown'),
      }));
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      {/* 히어로 — 아바타 + 정체성(이름/권한/이메일) */}
      {variant === 'full' && (
      <section className="settings-section profile-hero" aria-label={tSettings('settings.profile.photo_heading')}>
        <AvatarUploader
          userId={user.id}
          name={name || user.name}
          value={avatarUrl}
          onChange={onAvatarChange}
          disabled={saving}
          readOnly /* 2026-09-13 — 사진은 인사에서 관리자가 고친다 */
        />
        <div className="profile-hero-meta">
          <div className="profile-hero-name">{name || user.name}</div>
          <div className="profile-hero-badges">
            <span className="settings-role-badge">{tSettings(ROLE_LABEL_KEY[user.role])}</span>
          </div>
          <div className="profile-hero-email">
            <Mail size={14} aria-hidden />
            <span>{email || '—'}</span>
          </div>
        </div>
      </section>
      )}

      {/* 기본 정보 — 편집 가능 필드 */}
      <section className="settings-section" aria-labelledby="prof-basic-h2">
        <h2 id="prof-basic-h2">{tSettings('settings.profile.card_basic')}</h2>

        <div className="profile-grid">
          <div className="profile-field profile-field--full">
            <label htmlFor="prof-name"><User size={14} aria-hidden /> {tSettings('settings.profile.label_name')}</label>
            {/* ★ 직위와 같은 방식 — 칸은 남기고 **읽기 전용**, 왜 못 고치는지를 곁에 적는다
                (2026-09-13 사용자 결정: 이름·영문 이름·생일·사진은 인사에서 관리자가 고친다). */}
            <input id="prof-name" type="text" value={name} readOnly disabled
              aria-describedby="prof-hr-locked-why"
              onChange={(e) => setName(e.target.value)} />
            <p id="prof-hr-locked-why" className="profile-hint">
              {tSettings('settings.profile.hr_locked')}
            </p>
          </div>
          <div className="profile-field">
            <label htmlFor="prof-position"><Briefcase size={14} aria-hidden /> {tSettings('settings.profile.label_position')}</label>
            {/* ★ 칸을 지우지 않고 **읽기 전용**으로 둔다 — 없애면 「내 직위가 무엇인지」 도
                못 보게 된다. 왜 못 고치는지를 곁에 적는다. */}
            <input
              id="prof-position" type="text" value={position} readOnly disabled
              aria-describedby="prof-position-why"
              placeholder={tSettings('settings.profile.placeholder_position')}
            />
            <p id="prof-position-why" className="profile-hint">
              {tSettings('settings.profile.position_readonly')}
            </p>
          </div>
          <div className="profile-field">
            <label htmlFor="prof-phone"><Phone size={14} aria-hidden /> {tSettings('settings.profile.label_phone')}</label>
            <input id="prof-phone" type="text" value={phone}
              onChange={(e) => setPhone(e.target.value)} placeholder={tSettings('settings.profile.placeholder_phone')} />
          </div>

          {/* 새 항목 6종 (migration 106) — 전부 선택 입력. 비워 두면 프로필에 그 줄이 안 나온다. */}
          <div className="profile-field">
            <label htmlFor="prof-name-en">{tSettings('settings.profile.label_name_en')}</label>
            <input id="prof-name-en" type="text" value={nameEn} readOnly disabled
              aria-describedby="prof-hr-locked-why"
              onChange={(e) => setNameEn(e.target.value)} placeholder={tSettings('settings.profile.ph_name_en')} />
          </div>
          <div className="profile-field">
            <label htmlFor="prof-extension">{tSettings('settings.profile.label_extension')}</label>
            <input id="prof-extension" type="text" value={extension}
              onChange={(e) => setExtension(e.target.value)} placeholder={tSettings('settings.profile.ph_extension')} />
          </div>
          <div className="profile-field">
            <label htmlFor="prof-desk">{tSettings('settings.profile.label_desk')}</label>
            <input id="prof-desk" type="text" value={deskLocation}
              onChange={(e) => setDeskLocation(e.target.value)} placeholder={tSettings('settings.profile.ph_desk')} />
          </div>
          <div className="profile-field">
            <label htmlFor="prof-birthday">{tSettings('settings.profile.label_birthday')}</label>
            <input id="prof-birthday" type="text" value={birthday} maxLength={5} readOnly disabled
              aria-describedby="prof-hr-locked-why"
              onChange={(e) => setBirthday(e.target.value)} placeholder={tSettings('settings.profile.ph_birthday')} />
          </div>
          <div className="profile-field">
            <label htmlFor="prof-timezone">{tSettings('settings.profile.label_timezone')}</label>
            <input id="prof-timezone" type="text" value={timezone}
              onChange={(e) => setTimezone(e.target.value)} placeholder="Asia/Seoul" />
          </div>
          <div className="profile-field profile-field--full">
            <label htmlFor="prof-bio">{tSettings('settings.profile.label_bio')}</label>
            <input id="prof-bio" type="text" value={bio} maxLength={200}
              onChange={(e) => setBio(e.target.value)} placeholder={tSettings('settings.profile.ph_bio')} />
          </div>
        </div>

        <div className="settings-actions">
          <button type="button" className="settings-btn" onClick={onSave} disabled={saving}>
            {saving ? tSettings('settings.btn.saving') : tSettings('settings.btn.save')}
          </button>
        </div>

        {error && <p className="settings-error" role="alert">{error}</p>}
        {savedAt && !error && (
          <p className="settings-success settings-success--anim">
            <Check size={14} aria-hidden /> {tSettings('settings.msg.saved')}
          </p>
        )}
      </section>
    </>
  );
}
